import {
  BenefitDto,
  QuestionDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  DependentMCSurveyResponseDto,
  QuestionnaireRequestDto,
  SurveyResponseDto,
  SurveyResponsesRequestDto,
  UnumQuestionnaireRequestDto,
} from '../../../types';
import {
  DATE_SURVEY_TYPE,
  DEP_ONLY_TYPE,
  HEIGHT_SURVEY_TYPE,
  isInvalidData,
  MULTI_CHOICE_SURVEY_TYPE,
  MULTI_SELECT_SURVEY_TYPE,
  SUB_AND_DEP_TYPE,
  SUB_ONLY_TYPE,
} from 'enrollment/features/Questionnaire/utils';
import {
  DEPENDENT_ID_INDEX,
  QUESTION_ID_INDEX,
  RESPONSE_ID_INDEX,
  UNUM_PARENT_LOOKUPS,
} from './constants';

interface MapRequestDataProps {
  questionnaireResponse: CategoryResponseDto | undefined;
  enrollmentContextType: string;
  questionIds?: number[];
}

export const mapRequestData = ({
  questionnaireResponse,
  enrollmentContextType,
  questionIds,
}: MapRequestDataProps) => {
  if (isInvalidData(questionnaireResponse)) {
    return [];
  }
  const data = questionnaireResponse?.data.benefits as BenefitDto[];
  const surveyBenefits = data.filter((benefit) => {
    return (
      benefit.org_benefit_survey_questions &&
      benefit.org_benefit_survey_questions.length > 0 &&
      benefit.plans &&
      benefit.plans.length > 0
    );
  });
  const retval = surveyBenefits.map((benefit) => {
    if (!benefit.org_benefit_survey_questions || !benefit.plans) {
      // forces for undefined TS check
      return {} as QuestionnaireRequestDto;
    }
    const orgBenefitId = benefit.benefit.org_benefit_id;
    const orgPlanId = benefit.plans[0].plan_id;
    const coverageLevelId = benefit.plans[0].level_id;
    const surveyResponses = getResponsesForBenefit(
      benefit.org_benefit_survey_questions,
      questionIds
    );

    return {
      enrollment_context_type: enrollmentContextType,
      org_benefit_id: orgBenefitId,
      election: {
        org_plan_id: orgPlanId,
        coverage_level_id: coverageLevelId,
        survey_responses: surveyResponses,
      },
      life_event_completed: false,
      benefit_category: 'Lifestyle',
    } as QuestionnaireRequestDto;
  });
  return retval;
};

export const mapUnumSurveyRequestData = (
  questionnaireResponse: CategoryResponseDto | undefined,
  benefitId: number | undefined
) => {
  if (isInvalidData(questionnaireResponse) || !Boolean(benefitId)) {
    return { id: benefitId } as UnumQuestionnaireRequestDto;
  }

  const data = questionnaireResponse?.data.benefits as BenefitDto[];
  const surveyBenefitIndex = data.findIndex((benefit) => {
    return benefit.benefit.org_benefit_id === benefitId;
  });
  const benefitQuestions =
    data[surveyBenefitIndex].org_benefit_survey_questions;

  if (surveyBenefitIndex < 0) {
    return {} as UnumQuestionnaireRequestDto;
  } else if (!benefitQuestions) {
    // forces for undefined TS check
    return {} as UnumQuestionnaireRequestDto;
  }

  const questionnaireForm = document.getElementById(
    'questionnaire-form'
  ) as HTMLElement;
  const questionInputElems = Array.from(
    questionnaireForm.getElementsByTagName('input')
  ).filter((elem) => {
    return elem.checked;
  });
  const elementResponses = {} as { [key: string | number]: string };
  questionInputElems.forEach((input) => {
    const ids = getQuestionIds(input.id);
    elementResponses[ids[QUESTION_ID_INDEX]] = input.value;
  });

  const retval = {
    answer: {},
    answer_id: {},
  } as UnumQuestionnaireRequestDto;
  retval.id = benefitId;
  data[surveyBenefitIndex].org_benefit_survey_questions?.forEach((question) => {
    const answer = elementResponses[question.question_id];
    if (answer) {
      retval['answer'] = {
        ...retval['answer'],
        [question.lookup_code]: answer,
      };
    }

    if (question.valid_responses) {
      question.valid_responses.forEach((response) => {
        retval['answer_id'] = {
          ...retval['answer_id'],
          [question.lookup_code]: {
            ...retval['answer_id'][question.lookup_code],
            [response.value]: response.response_id,
          },
        };

        // the response needs Yes/No answers to potentially hidden (unanswered) questions
        // so we need to force populate these answers
        const relatedQuestionIndex = UNUM_PARENT_LOOKUPS.findIndex(
          (lookup) => lookup === question.lookup_code
        );
        if (relatedQuestionIndex >= 0 && response.value === 'Yes') {
          const relatedLookup = `${UNUM_PARENT_LOOKUPS[relatedQuestionIndex].split('_')[0]}_HasGreenCard`;
          retval['answer'] = { ...retval['answer'], [relatedLookup]: 'No' };
        }
      });
    }
  });

  return retval;
};

const getResponsesForBenefit = (
  questions: QuestionDto[],
  questionIds?: number[]
) => {
  const questionnaireForm = document.getElementById(
    'questionnaire-form'
  ) as HTMLElement;

  return questions.map((question) => {
    const questionResponses = Array.from(
      questionnaireForm.getElementsByTagName('input')
    ).filter((input) => {
      const ids = getQuestionIds(input.id);
      return ids[QUESTION_ID_INDEX] === question.question_id;
    });

    if (questionIds && !questionIds.includes(question.question_id)) {
      return undefined;
    }

    const visibleIds = questionResponses.map((input) => {
      return getQuestionIds(input.id);
    });
    if (
      !visibleIds.some((ids) => ids[QUESTION_ID_INDEX] === question.question_id)
    ) {
      return undefined;
    }

    // now we have each input element for this question - loop
    const response = {
      question_id: question.question_id,
      responses: {},
    };
    const dependentResponses: { [key: number]: string } = {};
    const dependentMCResponses: DependentMCSurveyResponseDto = {};
    const subscriberResponses: SurveyResponseDto[] = [];
    const heightResponses: {
      heightFt: number;
      heightIn: number;
      depId?: number;
    }[] = [];
    questionResponses.forEach((userResponse) => {
      const ids = getQuestionIds(userResponse.id);
      const responseValue =
        question.question_type === DATE_SURVEY_TYPE
          ? formatDateStringRequest(userResponse.value)
          : userResponse.value;

      if (
        ![
          MULTI_CHOICE_SURVEY_TYPE,
          MULTI_SELECT_SURVEY_TYPE,
          HEIGHT_SURVEY_TYPE,
        ].includes(question.question_type)
      ) {
        switch (question.targeted_at_type) {
          case SUB_AND_DEP_TYPE:
            const depId = Number(ids[DEPENDENT_ID_INDEX]);
            if (depId !== 0) {
              Object.assign(dependentResponses, {
                [depId]: responseValue,
              });
            } else {
              response.responses = {
                ...response.responses,
                subscriber_response: responseValue,
              };
            }
            break;

          case DEP_ONLY_TYPE:
            const depOnlyId = Number(Number(ids[DEPENDENT_ID_INDEX]));
            Object.assign(dependentResponses, {
              [depOnlyId]: responseValue,
            });
            break;

          case SUB_ONLY_TYPE:
            response.responses = {
              ...response.responses,
              subscriber_response: responseValue,
            };
            return;
        }
      } else if (question.question_type === HEIGHT_SURVEY_TYPE) {
        switch (question.targeted_at_type) {
          case SUB_AND_DEP_TYPE:
            const depId = Number(ids[DEPENDENT_ID_INDEX]);
            if (depId !== 0) {
              if (userResponse?.id.includes('ft')) {
                heightResponses.push({
                  heightFt: Number(responseValue),
                  heightIn: 0,
                  depId: depId,
                });
              } else {
                heightResponses.push({
                  heightFt: 0,
                  heightIn: Number(responseValue),
                  depId: depId,
                });
              }
            } else {
              if (userResponse?.id.includes('ft')) {
                heightResponses.push({
                  heightFt: Number(responseValue),
                  heightIn: 0,
                });
              } else {
                heightResponses.push({
                  heightFt: 0,
                  heightIn: Number(responseValue),
                });
              }
            }
            break;

          case DEP_ONLY_TYPE:
            const depOnlyId = Number(Number(ids[DEPENDENT_ID_INDEX]));
            if (userResponse?.id.includes('ft')) {
              heightResponses.push({
                heightFt: Number(responseValue),
                heightIn: 0,
                depId: depOnlyId,
              });
            } else {
              heightResponses.push({
                heightFt: 0,
                heightIn: Number(responseValue),
                depId: depOnlyId,
              });
            }
            break;

          case SUB_ONLY_TYPE:
            if (userResponse?.id.includes('ft')) {
              heightResponses.push({
                heightFt: Number(responseValue),
                heightIn: 0,
              });
            } else {
              heightResponses.push({
                heightFt: 0,
                heightIn: Number(responseValue),
              });
            }
            return;
        }
      } else if (userResponse.checked) {
        // radio / checkbox
        switch (question.targeted_at_type) {
          case SUB_AND_DEP_TYPE:
            const depId = Number(ids[DEPENDENT_ID_INDEX]);
            if (depId !== 0) {
              if (dependentMCResponses[depId]) {
                dependentMCResponses[depId].push({
                  response_id: Number(ids[RESPONSE_ID_INDEX]),
                  value: responseValue,
                  display_order: Number(userResponse.dataset.displayOrder),
                });
              } else {
                Object.assign(dependentMCResponses, {
                  [depId]: [
                    {
                      response_id: Number(ids[RESPONSE_ID_INDEX]),
                      value: responseValue,
                      display_order: Number(userResponse.dataset.displayOrder),
                    },
                  ],
                });
              }
            } else {
              subscriberResponses.push({
                response_id: Number(ids[RESPONSE_ID_INDEX]),
                value: responseValue,
                display_order: Number(userResponse.dataset.displayOrder),
              });
            }
            break;

          case DEP_ONLY_TYPE:
            const depOnlyId = Number(ids[DEPENDENT_ID_INDEX]);
            if (dependentMCResponses[depOnlyId]) {
              dependentMCResponses[depOnlyId].push({
                response_id: Number(ids[RESPONSE_ID_INDEX]),
                value: responseValue,
                display_order: Number(userResponse.dataset.displayOrder),
              });
            } else {
              Object.assign(dependentMCResponses, {
                [depOnlyId]: [
                  {
                    response_id: Number(ids[RESPONSE_ID_INDEX]),
                    value: responseValue,
                    display_order: Number(userResponse.dataset.displayOrder),
                  },
                ],
              });
            }
            break;

          case SUB_ONLY_TYPE:
            subscriberResponses.push({
              response_id: Number(ids[RESPONSE_ID_INDEX]),
              value: responseValue,
              display_order: Number(userResponse.dataset.displayOrder),
            });
            break;
        }
      }
    });

    if (question.targeted_at_type !== DEP_ONLY_TYPE) {
      if (
        question.question_type === MULTI_CHOICE_SURVEY_TYPE ||
        question.question_type === MULTI_SELECT_SURVEY_TYPE
      ) {
        response.responses = {
          ...response.responses,
          subscriber_response: subscriberResponses,
        };
      } else if (question.question_type === HEIGHT_SURVEY_TYPE) {
        response.responses = {
          ...response.responses,
          subscriber_response: getHeightValue(heightResponses),
        };
        Object.assign(
          dependentResponses,
          getHeightValue(heightResponses, true)
        );
      }
    }

    if ([SUB_AND_DEP_TYPE, DEP_ONLY_TYPE].includes(question.targeted_at_type)) {
      const depValues =
        Object.keys(dependentMCResponses).length > 0
          ? dependentMCResponses
          : dependentResponses;
      // add dependent_responses if applicable
      response.responses = {
        ...response.responses,
        dependent_responses: depValues,
      };
    }
    return response as SurveyResponsesRequestDto;
  });
};

const getQuestionIds = (inputId: string) => {
  const responseId = inputId.substring(
    inputId.indexOf('r') + 1,
    inputId.indexOf('dep') - 1
  );
  const questionId = Number(
    inputId.substring(inputId.indexOf('q') + 1, inputId.indexOf('_'))
  );
  let dependentId = '';
  if (inputId.includes('dep')) {
    dependentId = inputId.substring(inputId.indexOf('dep') + 3);
  }

  return [questionId, responseId, dependentId];
};

const getHeightValue = (
  heightResponses: {
    heightFt: number;
    heightIn: number;
    depId?: number;
  }[],
  hasDep?: boolean
) => {
  let heightFt = 0;
  let heightIn = 0;

  const depHeightsInital: {
    heightFt: number;
    heightIn: number;
    depId: number;
  }[] = [];
  const depHeightsRetval: { [key: number]: number } = {};
  heightResponses.forEach((height) => {
    if (hasDep && height.depId) {
      const depIndex = depHeightsInital.findIndex((depHeight) => {
        return depHeight.depId === height.depId;
      });
      if (depIndex === -1) {
        depHeightsInital.push({
          heightFt: height.heightFt,
          heightIn: height.heightIn,
          depId: height.depId,
        });
      } else {
        // height or ft exists for dep already, check and then add missing
        if (depHeightsInital[depIndex].heightFt === 0) {
          depHeightsInital[depIndex] = {
            heightFt: height.heightFt,
            heightIn: depHeightsInital[depIndex].heightIn,
            depId: height.depId,
          };
        } else {
          depHeightsInital[depIndex] = {
            heightFt: depHeightsInital[depIndex].heightFt,
            heightIn: height.heightIn,
            depId: height.depId,
          };
        }
      }
    } else if (!height.depId) {
      // subscriber
      if (height.heightFt === 0) {
        heightIn = Math.max(height.heightIn, heightIn);
      } else {
        heightFt = Math.max(height.heightFt, heightFt);
      }
    }
  });

  if (hasDep) {
    depHeightsInital.forEach((depHeight) => {
      Object.assign(depHeightsRetval, {
        [depHeight.depId]: calculateHeightValue(
          depHeight.heightFt,
          depHeight.heightIn
        ),
      });
    });
    return depHeightsRetval;
  } else {
    return calculateHeightValue(heightFt, heightIn);
  }
};

const calculateHeightValue = (heightFt: number, heightIn: number) => {
  return heightFt * 12 + heightIn;
};

const formatDateStringRequest = (date: string) => {
  const dateArr = date.split('-');
  return `${dateArr[0]}/${dateArr[1]}/${dateArr[2]}`;
};
